import React, { useState, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  showPopover,
  hidePopover,
} from '@rsos/components/capstone/common/Map/useDeckGL/MapPopover/mapPopoverSlice';
import {
  trackMarkerClick,
  trackMarkerHover,
} from '@rsos/components/capstone/common/Map/useDeckGL/spatialTracking';
import {
  DYNAMIC_LAYERS_CARD,
  setCardsToRender,
  setMapCardData,
} from '@rsos/reduxDataStore/src/features/mapCard/mapCardSlice';
import DynamicLayerGL from './DynamicLayerGL';

/**
 * useDynamicLayers returns an optimized selection of dynamicLayers from redux.
 * @returns {Object} dynamicLayers
 */
const useDynamicLayers = () => {
  return useSelector(
    state => state?.spatialLayers?.dynamicLayers || [],
    (a, b) => {
      // NOTE this function returns true if the prev and
      // current values are equal. False if otherwise. Used
      // to make sure only changes are triggered when values
      // in the array are different. Without this, useSelector
      // will trigger re-renders on any change to dynamicLayers,
      // even if the data is exactly the same as before.
      if (!Array.isArray(a) || !Array.isArray(b)) {
        return false;
      }

      if (a.length !== b.length) {
        return false;
      }

      return JSON.stringify(a) === JSON.stringify(b);
    },
  );
};

const DynamicLayersGL = ({ overlay, isMapIconDisabled }) => {
  const dispatch = useDispatch();
  const dynamicLayers = useDynamicLayers();
  const [activeMarkerId, setActiveMarkerId] = useState(null);
  const [hoveredMarkerInfo, setHoveredMarkerInfo] = useState(null);

  const openedMarkerId = useSelector(state => {
    return state.mapCard?.mapCardData?.spatialObject?.external_id;
  });

  const resetPopoverStates = useCallback(() => {
    setHoveredMarkerInfo(null);
    dispatch(hidePopover());
  }, [dispatch, setHoveredMarkerInfo]);

  useEffect(() => {
    if (!openedMarkerId) {
      setActiveMarkerId(null);
      resetPopoverStates();
    }
  }, [openedMarkerId, resetPopoverStates]);

  // Show popover when hovering over a marker. Update when its position changes.
  useEffect(() => {
    if (hoveredMarkerInfo) {
      const spatialLayer = dynamicLayers.find(layer => {
        return layer.key === hoveredMarkerInfo.spatialLayerKey;
      });
      if (spatialLayer) {
        const spatialObject = spatialLayer.spatial_objects.find(obj => {
          return obj.external_id === hoveredMarkerInfo.markerId;
        });
        if (spatialObject) {
          dispatch(
            showPopover({
              spatialLayerKey: hoveredMarkerInfo.spatialLayerKey,
              spatialLayer,
              spatialObject,
            }),
          );
        }
      }
    }
  }, [dispatch, activeMarkerId, hoveredMarkerInfo, dynamicLayers]);

  const handleMarkerClick = (info, spatialLayerKey) => {
    setActiveMarkerId(info?.object?.external_id);

    const spatialLayer = dynamicLayers.find(
      layer => layer.key === spatialLayerKey,
    );
    trackMarkerClick(spatialLayerKey);
    if (spatialLayer) {
      dispatch(
        setMapCardData({
          layerData: spatialLayer,
          spatialObject: info?.object,
        }),
      );
      dispatch(setCardsToRender(DYNAMIC_LAYERS_CARD));
    }
  };

  const handleMarkerHover = (info, spatialLayerKey) => {
    if (info.object) {
      overlay.setCursorPointer();
      setHoveredMarkerInfo({
        markerId: info?.object?.external_id,
        spatialLayerKey,
      });
      trackMarkerHover(spatialLayerKey);
    } else {
      overlay.setCursorDefault();
      resetPopoverStates();
    }
  };

  return (
    <>
      {dynamicLayers.map((spatialLayer, idx) => (
        <DynamicLayerGL
          key={`${spatialLayer.key}-${idx}`}
          activeMarkerId={activeMarkerId}
          overlay={overlay}
          data={spatialLayer.spatial_objects}
          spatialLayerID={spatialLayer.key}
          visible={spatialLayer.isChecked}
          onClick={handleMarkerClick}
          onHover={handleMarkerHover}
          isMapIconDisabled={isMapIconDisabled}
        />
      ))}
    </>
  );
};

export default DynamicLayersGL;
